/**
 * Overlay recipes (Modal, Drawer, Menu, Popover, Tooltip). Flat platform
 * (ENG-2145): no drop shadows — borders separate floating surfaces.
 *
 * Modal shell is white (same as Drawer / TopBar chrome); structural fills
 * inside (table headers) use Pearl via the Table recipe.
 */
const chromeBorder = {
	borderColor: 'gray.300',
	flexShrink: 0,
} as const;

export const Modal = {
	baseStyle: (props: { scrollBehavior?: string }) => ({
		dialog: {
			borderRadius: '2xl',
			bg: 'white',
			boxShadow: 'none',
		},
		header: {
			fontFamily: 'body',
			fontWeight: 'semibold',
			fontSize: 'lg',
			color: 'fg.default',
			bg: 'white',
			borderTopRadius: '2xl',
			borderBottom: props.scrollBehavior === 'inside' ? '1px solid' : undefined,
			...chromeBorder,
			borderColor: 'border.subtle',
		},
		// The dialog's rounded corners only clip its own background — children
		// paint over them. Like the header's borderTopRadius, the bottom-most
		// slice (footer, or body when footerless) must round its own corners
		// or the white fill squares off the dialog's bottom edge.
		body: {
			bg: 'white',
			borderBottomRadius: '2xl',
		},
		footer: {
			bg: 'white',
			borderTop: '1px solid',
			...chromeBorder,
			borderColor: 'border.subtle',
			borderBottomRadius: '2xl',
		},
		overlay: {
			bg: 'blackAlpha.500',
		},
	}),
};

export const Drawer = {
	baseStyle: (props: { placement?: string }) => ({
		dialog: {
			bg: 'white',
			top: '0 !important',
			bottom: '0 !important',
			height: 'auto !important',
			maxHeight: 'none !important',
			...(props.placement === 'left'
				? { borderRightWidth: '1px', borderRightColor: 'border.subtle' }
				: { borderLeftWidth: '1px', borderLeftColor: 'border.subtle' }),
		},
		header: {
			fontFamily: 'body',
			fontWeight: 'semibold',
			color: 'fg.default',
			bg: 'white',
			borderBottom: '1px solid',
			borderColor: 'border.subtle',
			flexShrink: 0,
		},
		footer: {
			bg: 'white',
			borderTop: '1px solid',
			...chromeBorder,
		},
	}),
};

export const Menu = {
	baseStyle: {
		list: {
			padding: '0.25rem',
			borderRadius: 'lg',
			borderColor: 'border.default',
			boxShadow: 'none',
		},
		item: {
			borderRadius: 'md',
			_focus: { bg: 'bg.subtle' },
			_hover: { bg: 'bg.subtle' },
		},
	},
} as const;

export const Popover = {
	baseStyle: {
		content: {
			borderRadius: 'xl',
			borderColor: 'border.default',
			boxShadow: 'none',
			_focusVisible: { boxShadow: 'none' },
		},
	},
} as const;

export const Tooltip = {
	baseStyle: {
		bg: 'midnight',
		color: 'white',
		borderRadius: 'md',
		fontSize: 'xs',
		px: 2,
		py: 1,
	},
} as const;
